import { useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import { useUserPreferences } from '../contexts/UserPreferencesContext';

/**
 * Available date format options for user preferences.
 * Each entry has a `value` (dayjs format token) and a `label` for display.
 */
export const DATE_FORMAT_OPTIONS = [
  { value: 'DD.MM.YYYY', label: 'DD.MM.YYYY' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
  { value: 'DD-MMM-YYYY', label: 'DD-MMM-YYYY' },
];

/**
 * Hook to get the user's preferred date format and formatting helpers.
 *
 * Returns:
 *   dateFormat       – the dayjs format string (e.g. 'DD.MM.YYYY')
 *   dateTimeFormat   – date format + time (e.g. 'DD.MM.YYYY HH:mm:ss')
 *   formatDate       – (value) => formatted date string (date only)
 *   formatDateTime   – (value) => formatted date+time string
 */
const useDateFormat = () => {
  const { preferences } = useUserPreferences();

  const dateFormat = preferences.dateFormat || 'DD.MM.YYYY';
  const dateTimeFormat = `${dateFormat} HH:mm:ss`;

  const formatDate = useCallback(
    (value) => {
      if (value === null || value === undefined || value === '') return '';
      const d = dayjs(value);
      if (!d.isValid()) return '';
      return d.format(dateFormat);
    },
    [dateFormat]
  );

  const formatDateTime = useCallback(
    (value) => {
      if (value === null || value === undefined || value === '') return '—';
      const d = dayjs(value);
      if (!d.isValid()) return '—';
      return d.format(dateTimeFormat);
    },
    [dateTimeFormat]
  );

  return useMemo(
    () => ({ dateFormat, dateTimeFormat, formatDate, formatDateTime }),
    [dateFormat, dateTimeFormat, formatDate, formatDateTime]
  );
};

export default useDateFormat;
